/**
 * "Gardens growing on Grow": the newest public gardens, live from Gardens on
 * Grow (the /gardens listing), in a strip that scrolls sideways, with
 * "Browse all gardens" under it.
 *
 * Each card is a garden's cover photo (a leaf tile without one), "Amy's
 * garden", "@amy" and its counts, and opens the garden's page (/@amy). Only
 * what the listing sends is drawn, and it sends only what a garden's own
 * public page already shows.
 *
 * While the list loads, when it fails, or when no garden is public, the
 * section draws nothing: the page reads the same without it. A failure here
 * is not the visitor's to hear about (the page's job is the rest).
 *
 * A visitor from the owner's own Instagram or Facebook (?from=ig, ?from=fb)
 * sees her garden first (facts.ownerSlug, the server's landing.OWNER_SLUG), when the listing has it; the
 * others keep the listing's order. The listing decides which gardens there
 * are; this only puts hers in front for them (landingFacts ownerFirst).
 *
 * Where: the landing page (landingSections puts it after the hero, or, on the
 * remodelled page, before the price).
 * API: GET /gardens/directory?sort=recent&page=1 (getGardenDirectory).
 * Settings: LANDING_GARDENS_MAX (tunables/landing), the most cards shown;
 * the owner's garden is the landing read's ownerSlug (GET /billing/landing).
 * The remodelled page (landing.V2) says more in its body (landing.v2.gardens.body):
 * that a gardener can get their own garden page, with the plants they
 * choose, and that nothing is public until they say so.
 *
 * Words (en/es): landing.gardens.title, .body, .label, .browse; landing.v2.gardens.body;
 * gardenPage.page.titleOf, .title, .count.*, .noPhoto.
 *
 * @module Gardens
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Sprout } from 'lucide-react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getGardenDirectory, type DirectoryGarden } from '@/shared/api/gardens';
import { LANDING_GARDENS_MAX } from '@/config/tunables/landing';
import { apiSrc } from '@/features/share/lib/gardenPage';
import { isFollower, ownerFirst } from '../lib/landingFacts';
import type { LandingSectionProps } from '../landingTypes';

/** The React Query key for the landing page's gardens. */
export const LANDING_GARDENS_QUERY_KEY = ['landing', 'gardens'] as const;

/**
 * One garden as a card that opens its page.
 *
 * @param props.garden - The garden as the listing sent it.
 */
const GardenCard: React.FC<{ garden: DirectoryGarden }> = ({ garden }) => {
  const { reactTranslate: t } = useTranslation();
  const [broken, setBroken] = React.useState(false);
  const title = garden.name ? t('gardenPage.page.titleOf', { name: garden.name }) : t('gardenPage.page.title');
  const counts = [
    garden.plants > 0 ? t(garden.plants === 1 ? 'gardenPage.page.count.plants_one' : 'gardenPage.page.count.plants', { count: garden.plants }) : null,
    garden.beds > 0 ? t(garden.beds === 1 ? 'gardenPage.page.count.beds_one' : 'gardenPage.page.count.beds', { count: garden.beds }) : null,
  ].filter(Boolean).join(' · ');
  const src = garden.cover?.src ?? null;
  return (
    <li className="w-56 shrink-0 snap-start">
      <Link
        to={`/@${garden.slug}`}
        className="block h-full overflow-hidden rounded-2xl border bg-card hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {src && !broken ? (
          <img src={apiSrc(src)} alt="" loading="lazy" draggable={false} onError={() => setBroken(true)} className="block aspect-[4/3] w-full object-cover" />
        ) : (
          <span className="flex aspect-[4/3] w-full items-center justify-center bg-muted" role="img" aria-label={t('gardenPage.page.noPhoto')}>
            <Sprout className="h-10 w-10 text-primary/35" aria-hidden="true" />
          </span>
        )}
        <span className="block space-y-0.5 px-3 py-2.5">
          <span className="block font-grow text-base font-bold leading-snug [overflow-wrap:anywhere]">{title}</span>
          {/* The handle only when the name does not say it already ("@amy's garden"). */}
          {!String(title).includes(`@${garden.slug}`) && <span className="block truncate text-sm text-muted-foreground">@{garden.slug}</span>}
          {counts && <span className="block text-sm text-muted-foreground">{counts}</span>}
        </span>
      </Link>
    </li>
  );
};

/**
 * The gardens strip; nothing until there is a public garden to show.
 *
 * @param props.arrival - How the visitor came: from the owner's Instagram or Facebook, her garden leads.
 * @param props.facts - The landing read: the owner's garden address (ownerSlug).
 * @param props.v2 - Whether to say the remodelled page's body.
 */
export const Gardens: React.FC<LandingSectionProps> = ({ arrival, facts, v2 }) => {
  const { reactTranslate: t } = useTranslation();
  const { data } = useQuery({
    queryKey: LANDING_GARDENS_QUERY_KEY,
    queryFn: () => getGardenDirectory({ sort: 'recent', page: 1 }),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const listed = data?.gardens ?? [];
  // Her followers see her garden first; everyone else, the newest first.
  const gardens = (isFollower(arrival) ? ownerFirst(listed, facts.ownerSlug) : listed).slice(0, LANDING_GARDENS_MAX);
  if (gardens.length === 0) return null;
  return (
    <section className="px-4 pb-6 pt-12 md:px-8 md:pb-10 md:pt-20">
      <div className="mx-auto max-w-5xl space-y-4">
        <h2 className="font-grow text-3xl font-extrabold leading-[1.08] tracking-tight [text-wrap:balance] md:text-[2.6rem]">{t('landing.gardens.title')}</h2>
        <p className="max-w-prose">{t(v2 ? 'landing.v2.gardens.body' : 'landing.gardens.body')}</p>
        <ul
          className="-mx-1 flex snap-x snap-mandatory gap-3 overflow-x-auto px-1 pb-3"
          tabIndex={0}
          aria-label={t('landing.gardens.label')}
        >
          {gardens.map((garden) => <GardenCard key={garden.slug} garden={garden} />)}
        </ul>
        <Link
          to="/gardens"
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border-2 border-primary px-4 font-semibold text-primary hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {t('landing.gardens.browse')}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </div>
    </section>
  );
};
